if (!customElements.get('product-quote')) {
  customElements.define(
    'product-quote',
    class ProductQuote extends HTMLElement {
      onVariantChangeUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.multiVariantSelects = this.querySelector('multi-variant-selects');
      }

      connectedCallback() {
        this.form = document.querySelector('form[id^="formful"]');
        this.updateOptions();
        this.updateFormInputs();
        this.initializeDynamicFieldsets();
        this.initializeDropZones();
        this.addEventListener('change', this.handleFieldsetChange.bind(this));
        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        console.log(selectedOptionValues);
        console.log(target);
        if (!this.contains(event.target)) return;

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && this.dataset.url !== productUrl;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: this.handleUpdateProductInfo(productUrl, selectedOptionValues,target),
        });
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl, selectedOptionValues,target) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);
          this.options = selectedOptionValues;
          this.updateFormInputs(target);

          if (!variant) {
            this.setUnavailable();
            return;
          }


          this.updateConfiguratorOptions(variant);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateConfiguratorOptions(variant) {
        const configurator = this.querySelector('product-configurator');
        if (configurator && variant) {
          configurator.dataset.price = variant.price;
          const firstDetails = configurator.querySelector('details');
          if (firstDetails) {
            firstDetails.open = true;
          }
        }
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        const selectors = ['price', 'Inventory', 'Sku']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      hideFormField(input) {
        const wrapper = input.closest('.formful__field-wrapper');
        if (wrapper) {
          wrapper.classList.add('hidden');
        }
      }

      initializeDropZones() {
        const dropZoneTemplate = document.querySelector('#drop-zone-template');
        if (!dropZoneTemplate) return;

        this.querySelectorAll('input[type="file"]').forEach((fileInput) => {
          const dropZone = dropZoneTemplate.content.cloneNode(true).querySelector('.drop-zone');
          const prompt = dropZone.querySelector('.drop-zone__prompt');
          const fileList = dropZone.querySelector('.drop-zone__files');

          fileInput.style.display = 'none';
          fileInput.parentNode.insertBefore(dropZone, fileInput);

          dropZone.addEventListener('click', () => fileInput.click());

          dropZone.addEventListener('dragover', (event) => {
            event.preventDefault();
            dropZone.classList.add('drop-zone--over');
          });

          dropZone.addEventListener('dragleave', () => dropZone.classList.remove('drop-zone--over'));

          dropZone.addEventListener('drop', (event) => {
            event.preventDefault();
            dropZone.classList.remove('drop-zone--over');
            fileInput.files = event.dataTransfer.files;
            this.updateFileList(fileInput, prompt, fileList);
          });

          fileInput.addEventListener('change', () => this.updateFileList(fileInput, prompt, fileList));
        });
      }

      updateFileList(fileInput, prompt, fileList) {
        if (fileInput.files.length > 0) {
          prompt.style.display = 'none';
          fileList.innerHTML = Array.from(fileInput.files)
            .map((file) => `<div class="drop-zone__file-name">${file.name}</div>`)
            .join('');
        } else {
          prompt.style.display = 'block';
          fileList.innerHTML = '';
        }
      }

      updateOptions() {
        this.options = Array.from(this.querySelectorAll('fieldset')).map((fieldset) => {
          return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked)?.value;
        });
      }

      updateFormInputs(target) {
        if (!this.form) return;

        if (target && target.closest) {
          const fieldset = target.closest('fieldset[data-option-name]');

          if (fieldset) {
            const optionName = fieldset.dataset.optionName;
            const allFieldsets = [...this.querySelectorAll('fieldset[data-option-name]')];
            const index = allFieldsets.indexOf(fieldset);

            if (index > -1) {
              const optionValue = target.dataset.optionValue;
              const input = this.form.querySelector(`input[data-label="${optionName}"]`);
              if (input) {
                input.value = optionValue || '';
                this.hideFormField(input);
              }
              return;
            }
          }
        }

        this.querySelectorAll('fieldset[data-option-name]').forEach((fieldset, index) => {
          const optionName = fieldset.dataset.optionName;
          const optionValue = this.options[index];

          const input = this.form.querySelector(`input[data-label="${optionName}"]`);
          if (input) {
            input.value = optionValue || '';
            this.hideFormField(input);
          }
        });
      }

      handleFieldsetChange(event) {
        const fieldset = event.target.closest('dynamic-fieldset');
        if (!fieldset) return;

        const dataId = fieldset.dataset.id;
        const input = fieldset.querySelector('input[type="number"], input[type="radio"]:checked');

        if (this.form && input) {
          const targetInput = this.form.querySelector(`input[data-key="${dataId}"]`);
          if (targetInput) {
            targetInput.value = input.value;
            this.hideFormField(targetInput);
          }
        }
      }

      initializeDynamicFieldsets() {
        if (!this.form) return;
        this.querySelectorAll('dynamic-fieldset').forEach((fieldset) => {
          const dataId = fieldset.dataset.id;
          const input = fieldset.querySelector('input[type="number"], input[type="radio"]:checked');
          if (input) {
            const { value } = input;
            const targetInput = this.form.querySelector(`input[data-key="${dataId}"]`);
            if (targetInput) {
              targetInput.value = value;
              this.hideFormField(targetInput);
            }
          }
        });
      }

    }
  );
}